/**
 * דוח תיקונים (D28, 12.10.2026) — נתוני ה־snapshot, המודל ונתוני הדגמה.
 *
 * - `FollowupReportData` הוא בדיוק מה שהשרת שומר ב־`report_snapshot.data` לסוג
 *   `followup_report` (`private.followup_report_data`, מיגרציה 20261012120000). המסמך מופק
 *   ממנו בלבד (RQ-I06).
 * - **מסמך רשות**, נפרד מדוח הביקור: רק מעקב התיקונים שנרשם בביקור הזה, על פריטים פתוחים
 *   מביקורים קודמים — ממצאים וסעיפים ״לא תקין״ בלי ממצא. כל פריט **בנוסח הסעיף** מגרסת התוכן
 *   של הביקור המקורי, בלי הקוד הפנימי.
 * - הכותרת סופרת מצבים, ולעולם אינה ״הכול נסגר״ אלא אם כולם אומתו (RQ-I14). ״דווח כתוקן״
 *   אינו אימות (C-10). הורדה ושיתוף אינם מסירה (RQ-F04).
 */

import {
  buildCorrectionsModel,
  type CorrectionsData,
  type CorrectionsModel,
  type FollowupItemData,
  type FollowupPhoto,
  type FollowupRecordData,
  type FollowupState,
} from './followup.ts';
import type { ReportEnvelope } from './visitReport.ts';

export interface FollowupReportData {
  schema: 1;
  kind: 'followup_report';
  organization: { name: string } | null;
  site: { id: string; name: string; address: string; is_demo: boolean };
  parties: { developer_name: string | null; main_contractor_name: string | null } | null;
  /** הביקור שבו נבדקו התיקונים. */
  visit: { id: string; visit_date: string; opened_at: string; submitted_at: string | null };
  corrections: CorrectionsData;
  people: Record<string, string>;
}

/** ה־Edge Function, שירות הדוחות והתצוגה המקדימה בוחרים תבנית לפי `kind`. */
export function isFollowupReportData(data: unknown): data is FollowupReportData {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as { kind?: unknown }).kind === 'followup_report' &&
    typeof (data as { corrections?: unknown }).corrections === 'object'
  );
}

export interface FollowupReportModel {
  data: FollowupReportData;
  envelope: ReportEnvelope;
  mode: 'draft' | 'approved';
  isDemo: boolean;
  corrections: CorrectionsModel;
  person: (id: string | null) => string;
}

export function buildFollowupReportModel(
  data: FollowupReportData,
  envelope: ReportEnvelope,
): FollowupReportModel {
  return {
    data,
    envelope,
    mode: envelope.approval === null ? 'draft' : 'approved',
    isDemo: data.site.is_demo,
    corrections: buildCorrectionsModel(data.corrections),
    person: (id) => (id === null ? 'לא ידוע' : (data.people[id] ?? 'חבר בארגון')),
  };
}

// --- נתוני הדגמה (סינתטיים, C-09) ------------------------------------------------------

/**
 * תרחישי PDF של דוח התיקונים (D28):
 * - `corrections-finding-and-item` — ממצא ״דווח כתוקן״ עם לפני/אחרי, סעיף בלי ממצא ״תוקן״ עם
 *   ״אחרי״, וסעיף בלי רשומה (״לא נבדק בביקור זה״);
 * - `corrections-all-verified` — כולם ״תוקן ואומת״ (הכותרת היחידה שאומרת ״תוקנו ואומתו״);
 * - `corrections-mixed` — שני דוחות מקוריים, כל המצבים, ממצא כללי;
 * - `corrections-fifty` — 50 פריטים (עמודים רבים, כותרת טבלה חוזרת);
 * - `corrections-no-after` — ״תוקן״ בלי תמונת ״אחרי״, ותמונת ״לפני״ שאינה באחסון;
 * - `corrections-old-version` — הביקור המקורי על גרסת תוכן 0.1: הנוסח של 0.1 מודפס;
 * - `corrections-injection` — טקסט עם תגיות ומספר זהות בכל שדה של הבקר.
 * - `corrections-chain-v2` — D32: דוח התיקונים של V2 בשרשרת (V1 ← V2 ← V3, כמו
 *   `followupScenarios.ts`): כל הפריטים מדוח מקורי אחד (V1) — ממצא ״תוקן ואומת״, סעיף בלי ממצא
 *   ״לא תוקן״ (שסומן שוב ״לא תקין״ בביקור זה), סעיף בלי ממצא בלי רשומה, וממצא כללי ״דווח כתוקן״.
 *   אחרי האישור כולם יוצאים מכרטיס התיקונים של V3 — לכן ההערה ״… אינו סגור, והטיפול בו נשאר
 *   באחריות המזמין״ חשובה כאן במיוחד.
 */
export type FollowupReportScenario =
  | 'corrections-finding-and-item'
  | 'corrections-all-verified'
  | 'corrections-mixed'
  | 'corrections-fifty'
  | 'corrections-no-after'
  | 'corrections-old-version'
  | 'corrections-injection'
  | 'corrections-chain-v2';

export const FOLLOWUP_REPORT_SCENARIOS: FollowupReportScenario[] = [
  'corrections-finding-and-item',
  'corrections-all-verified',
  'corrections-mixed',
  'corrections-fifty',
  'corrections-no-after',
  'corrections-old-version',
  'corrections-injection',
  'corrections-chain-v2',
];

const U = {
  a: '11111111-1111-4111-8111-111111111111',
  b: '22222222-2222-4222-8222-222222222222',
};

function uuid(prefix: string, n: number): string {
  const hex = n.toString(16).padStart(12, '0');
  return `${prefix.padEnd(8, '0').slice(0, 8)}-0000-4000-8000-${hex}`;
}

/** תמונת לפני/אחרי סינתטית. `observedAt` null — ״מועד לא ידוע״ (RQ-G06). */
export function sampleFollowupPhoto(
  n: number,
  caption: string,
  stored = true,
  observedAt: string | null = new Date(Date.UTC(2026, 8, 20 + (n % 8), 7, n % 60)).toISOString(),
): FollowupPhoto {
  return {
    id: uuid('fe', n),
    storage_path: `site/${uuid('fe', n)}/followup-${n}.jpg`,
    mime_type: 'image/jpeg',
    caption,
    observed_at: observedAt,
    observed_at_source: observedAt === null ? 'unknown' : 'device',
    stored,
  };
}

const SOURCE_1 = { id: uuid('pv', 1), date: '2026-09-14', approved: true };
const SOURCE_2 = { id: uuid('pv', 2), date: '2026-09-21', approved: false };
/** V1 של השרשרת (`corrections-chain-v2`); הביקור של הדוח הוא V2. */
const SOURCE_V1 = { id: uuid('pv', 11), date: '2026-09-15', approved: true };

function source(s: { id: string; date: string; approved: boolean }): FollowupItemData['source'] {
  return {
    visit_id: s.id,
    visit_date: s.date,
    report_number: `V-${s.date.replace(/-/g, '')}-${s.id.replace(/-/g, '').slice(0, 6).toUpperCase()}`,
    report_approved: s.approved,
  };
}

function record(state: FollowupState, extra: Partial<FollowupRecordData> = {}): FollowupRecordData {
  return {
    state,
    not_checked_reason: null,
    fix_reported_by_text: '',
    fix_reported_on: null,
    fix_report_text: '',
    evidence_source: 'none',
    note: '',
    recorded_by: U.b,
    recorded_at: '2026-10-01T08:10:00.000Z',
    ...extra,
  };
}

/** סעיף ״לא תקין״ בלי ממצא (D28 ס׳ 5). */
function itemOf(
  n: number,
  src: { id: string; date: string; approved: boolean },
  x: {
    chapter: string;
    number: string | null;
    title: string;
    note?: string;
    scheme?: 'standard' | 'plan_single';
    state?: 'not_ok' | 'needs_fix';
    followup?: FollowupRecordData | null;
    before?: FollowupPhoto[];
    after?: FollowupPhoto[];
  },
): FollowupItemData {
  return {
    kind: 'item',
    finding_id: null,
    source_visit_id: src.id,
    question_id: uuid('q', n),
    chapter_title: x.chapter,
    answer_scheme: x.scheme ?? 'standard',
    item_number: x.number,
    item_title: x.title,
    original_state: x.state ?? 'not_ok',
    original_note: x.note ?? '',
    finding_number: null,
    severity: null,
    description: null,
    location_text: '',
    area_name: null,
    source: source(src),
    followup: x.followup ?? null,
    before: x.before ?? [],
    after: x.after ?? [],
  };
}

/** ממצא ממוספר — על סעיף (עם נוסח הסעיף) או כללי (בלי סעיף). */
function findingOf(
  n: number,
  src: { id: string; date: string; approved: boolean },
  x: {
    findingNumber: number;
    description: string;
    severity: 'immediate' | 'high' | 'low' | 'normal';
    location?: string;
    chapter?: string;
    number?: string | null;
    title?: string;
    note?: string;
    followup?: FollowupRecordData | null;
    before?: FollowupPhoto[];
    after?: FollowupPhoto[];
  },
): FollowupItemData {
  const general = x.title === undefined;
  return {
    kind: 'finding',
    finding_id: uuid('f', n),
    source_visit_id: src.id,
    question_id: general ? null : uuid('q', n),
    chapter_title: general ? null : (x.chapter ?? null),
    answer_scheme: general ? null : 'standard',
    item_number: general ? null : (x.number ?? null),
    item_title: general ? null : (x.title ?? null),
    original_state: general ? null : 'not_ok',
    original_note: x.note ?? '',
    finding_number: x.findingNumber,
    severity: x.severity,
    description: x.description,
    location_text: x.location ?? '',
    area_name: null,
    source: source(src),
    followup: x.followup ?? null,
    before: x.before ?? [],
    after: x.after ?? [],
  };
}

/** פריטי הדגמה לדוח התיקונים ולפרק התיקונים בדוח הביקור. */
export function sampleCorrectionsData(
  scenario: FollowupReportScenario = 'corrections-finding-and-item',
): CorrectionsData {
  const railing = (followup: FollowupRecordData | null, after: FollowupPhoto[] = []) =>
    findingOf(1, SOURCE_1, {
      findingNumber: 3,
      description: 'חסר מעקה בשפת התקרה בקומה 4, לאורך כ־12 מ׳.',
      severity: 'high',
      location: 'קומה 4, צד מזרחי',
      chapter: 'עבודה בגובה, שפות ופתחים',
      number: '5.1',
      title: 'מעקות בשפות תקרה',
      note: 'מעקה זמני חסר',
      followup,
      before: [sampleFollowupPhoto(90, 'המעקה החסר — מהדוח הקודם')],
      after,
    });
  const opening = (followup: FollowupRecordData | null, after: FollowupPhoto[] = []) =>
    itemOf(2, SOURCE_1, {
      chapter: 'עבודה בגובה, שפות ופתחים',
      number: '5.2',
      title: 'פתחים ברצפה',
      note: 'פתח בקומה 3 ללא כיסוי',
      followup,
      before: [sampleFollowupPhoto(92, 'הפתח — מהדוח הקודם')],
      after,
    });
  const signs = (followup: FollowupRecordData | null) =>
    itemOf(3, SOURCE_1, {
      chapter: 'גידור, שילוט ודרכי גישה',
      number: '4.2',
      title: 'שילוט אזהרה',
      followup,
    });
  switch (scenario) {
    case 'corrections-finding-and-item':
      return {
        items: [
          signs(null),
          railing(
            record('reported_fixed_pending', {
              fix_reported_by_text: 'מנהל העבודה, יוסי',
              fix_reported_on: '2026-09-25',
              fix_report_text: 'הותקן מעקה זמני; מעקה קבוע יותקן בשבוע הבא.',
              evidence_source: 'verbal',
              note: 'לא ראיתי בעצמי — הקומה הייתה סגורה.',
            }),
            [sampleFollowupPhoto(91, 'תמונה שנשלחה מהאתר')],
          ),
          opening(record('verified_fixed', { evidence_source: 'inspector_observed' }), [
            sampleFollowupPhoto(93, 'הפתח מכוסה — ביקור זה'),
          ]),
        ],
      };
    case 'corrections-all-verified':
      return {
        items: [
          railing(record('verified_fixed', { evidence_source: 'inspector_observed' }), [
            sampleFollowupPhoto(91, 'אחרי — תקין'),
          ]),
          opening(record('verified_fixed', { note: 'הכיסוי קובע ומסומן.' }), [
            sampleFollowupPhoto(93, ''),
          ]),
        ],
      };
    case 'corrections-mixed':
      return {
        items: [
          signs(record('verified_not_fixed', { note: 'השלט עדיין חסר בשער 2.' })),
          railing(record('verified_fixed'), [sampleFollowupPhoto(91, 'אחרי')]),
          opening(
            record('not_checked', {
              not_checked_reason: 'area_inaccessible',
              note: 'הקומה סגורה.',
            }),
          ),
          findingOf(4, SOURCE_1, {
            findingNumber: 7,
            description: 'ערימת פסולת חוסמת את יציאת החירום בחניון.',
            severity: 'immediate',
            location: 'חניון B',
            followup: record('reported_fixed_pending', {
              fix_reported_by_text: 'קבלן הניקיון',
              fix_reported_on: '2026-09-29',
              evidence_source: 'site_photo',
            }),
            before: [sampleFollowupPhoto(94, 'הפסולת — לפני')],
            after: [sampleFollowupPhoto(95, 'תמונה מהקבלן', true, null)],
          }),
          itemOf(5, SOURCE_2, {
            chapter: 'תוכנית הבטיחות',
            number: '1.1',
            title: 'קיום ויישום תוכנית הבטיחות במועד הביקור',
            scheme: 'plan_single',
            state: 'needs_fix',
            note: 'התוכנית לא עודכנה אחרי שינוי השלב.',
            followup: record('verified_fixed', { evidence_source: 'document' }),
          }),
          itemOf(6, SOURCE_2, {
            chapter: 'ציוד מגן אישי',
            number: '3.1',
            title: 'קסדות מגן',
            followup: null,
          }),
        ],
      };
    case 'corrections-fifty': {
      const states: Array<FollowupState | null> = [
        'verified_fixed',
        'reported_fixed_pending',
        'verified_not_fixed',
        'not_checked',
        null,
      ];
      return {
        items: Array.from({ length: 50 }, (_, i) => {
          const state = states[i % 5]!;
          const followup =
            state === null
              ? null
              : record(state, {
                  fix_reported_by_text: state === 'reported_fixed_pending' ? 'מנהל העבודה' : '',
                  fix_reported_on: state === 'reported_fixed_pending' ? '2026-09-26' : null,
                });
          const src = i < 25 ? SOURCE_1 : SOURCE_2;
          return i % 3 === 0
            ? findingOf(100 + i, src, {
                findingNumber: 100 + i,
                description: `ממצא ${i + 1}: גידור חסר בקומה ${(i % 9) + 1}, קטע ${i + 2}.`,
                severity: (['immediate', 'high', 'low'] as const)[i % 3]!,
                location: `קומה ${(i % 9) + 1}`,
                chapter: 'גידור, שילוט ודרכי גישה',
                number: `4.${(i % 5) + 1}`,
                title: 'גידור היקפי',
                followup,
                before: [sampleFollowupPhoto(200 + i, '')],
                after: state === 'verified_fixed' ? [sampleFollowupPhoto(300 + i, '')] : [],
              })
            : itemOf(100 + i, src, {
                chapter: 'ציוד מגן אישי',
                number: `3.${(i % 5) + 1}`,
                title: `סעיף לדוגמה ${i + 1} — ציוד מגן לעובדים באזור ${(i % 7) + 1}`,
                note: i % 4 === 0 ? `הערה מהביקור הקודם לסעיף ${i + 1}` : '',
                followup,
              });
        }),
      };
    }
    case 'corrections-no-after':
      return {
        items: [
          opening(record('verified_fixed', { note: 'אומת בעין; לא צולם.' })),
          findingOf(7, SOURCE_1, {
            findingNumber: 9,
            description: 'סולם פגום בגרם המדרגות.',
            severity: 'low',
            chapter: 'עבודה בגובה, שפות ופתחים',
            number: '5.3',
            title: 'גרמי מדרגות',
            followup: record('verified_fixed'),
            before: [sampleFollowupPhoto(96, 'הסולם — לפני', false)],
          }),
        ],
      };
    case 'corrections-old-version':
      // הביקור המקורי על 0.1: C5-02 = ״פתחים ברצפה״ (ב־0.2 אותו קוד הוא סעיף אחר לגמרי).
      return {
        items: [
          itemOf(8, SOURCE_2, {
            chapter: 'עבודה בגובה, שפות ופתחים',
            number: '5.2',
            title: 'פתחים ברצפה',
            note: 'נבדק בביקור שנעשה על גרסת תוכן 0.1',
            followup: record('reported_fixed_pending', {
              fix_reported_by_text: 'מנהל העבודה',
              fix_reported_on: '2026-09-30',
            }),
          }),
        ],
      };
    case 'corrections-injection':
      return {
        items: [
          itemOf(9, SOURCE_1, {
            chapter: '<b>פרק</b> & "ציטוט"',
            number: '2.1',
            title: '<script>window.injected=5</script> סעיף',
            note: '<img src=x onerror="window.injected=6"> ת.ז. 123456789',
            followup: record('reported_fixed_pending', {
              fix_reported_by_text:
                '<img src=x onerror="window.injected=3"> מנהל העבודה ת.ז. 123456789',
              fix_reported_on: '2026-09-25',
              fix_report_text: '</td><td>שבירת טבלה',
              note: '<script>window.injected=4</script>',
            }),
            before: [sampleFollowupPhoto(97, '"><b>לפני</b>')],
            after: [sampleFollowupPhoto(98, '</figure><b>אחרי</b>')],
          }),
        ],
      };
    case 'corrections-chain-v2': {
      // V1 ← V2 של טבלת התרחישים המשותפת, בסדר של השרת: סעיפים לפי הצ׳קליסט, ממצא כללי בסוף.
      // תמונות ״לפני״ — מיום V1; ״אחרי״ — מיום V2 (הביקור של הדוח).
      const inV1 = '2026-09-15T07:10:00.000Z';
      const inV2 = '2026-10-01T07:20:00.000Z';
      return {
        items: [
          findingOf(11, SOURCE_V1, {
            findingNumber: 1,
            description: 'שלט האזהרה בשער הכניסה נפל ולא הוחזר למקומו.',
            severity: 'low',
            location: 'שער הכניסה הראשי',
            chapter: 'גידור, שילוט ודרכי גישה',
            number: '4.2',
            title: 'שילוט אזהרה',
            followup: record('verified_fixed', { evidence_source: 'inspector_observed' }),
            before: [sampleFollowupPhoto(110, 'השלט שנפל — מהדוח הקודם', true, inV1)],
            after: [sampleFollowupPhoto(111, 'השלט במקומו — ביקור זה', true, inV2)],
          }),
          itemOf(12, SOURCE_V1, {
            chapter: 'עבודה בגובה, שפות ופתחים',
            number: '5.1',
            title: 'מעקות בשפות תקרה',
            note: 'מעקה חסר בקומה 6, צד מערבי',
            followup: null,
          }),
          itemOf(13, SOURCE_V1, {
            chapter: 'עבודה בגובה, שפות ופתחים',
            number: '5.2',
            title: 'פתחים ברצפה',
            note: 'פתח בקומה 3 ללא כיסוי',
            followup: record('verified_not_fixed', {
              note: 'הפתח עדיין ללא כיסוי — סומן שוב ״לא תקין״ בצ׳קליסט של ביקור זה.',
            }),
            before: [sampleFollowupPhoto(112, 'הפתח — מהדוח הקודם', true, inV1)],
          }),
          findingOf(14, SOURCE_V1, {
            findingNumber: 2,
            description: 'פסולת בנייה חוסמת את דרך הגישה לרכב חירום.',
            severity: 'high',
            location: 'דרך הגישה הצפונית',
            followup: record('reported_fixed_pending', {
              fix_reported_by_text: 'מנהל העבודה',
              fix_reported_on: '2026-09-28',
              fix_report_text: 'הפסולת פונתה — לפי דיווח טלפוני.',
              evidence_source: 'verbal',
            }),
            before: [sampleFollowupPhoto(114, 'הפסולת — מהדוח הקודם', true, inV1)],
          }),
        ],
      };
    }
  }
}

export function sampleFollowupReportData(
  scenario: FollowupReportScenario = 'corrections-finding-and-item',
): FollowupReportData {
  const injection = scenario === 'corrections-injection';
  return {
    schema: 1,
    kind: 'followup_report',
    organization: { name: 'חכשורי בטיחות' },
    site: {
      id: uuid('s', 1),
      name: injection ? 'אתר <b>מודגש</b> & "ציטוט"' : 'אתר הדגמה א — מגדל A',
      address: 'רחוב הדגמה 12, עיר הדגמה',
      is_demo: true,
    },
    parties: {
      developer_name: 'מזמין הדגמה בע״מ',
      main_contractor_name: 'קבלן הדגמה (Demo Builders)',
    },
    visit: {
      id: uuid('v', 9),
      visit_date: '2026-10-01',
      opened_at: '2026-10-01T06:30:00.000Z',
      submitted_at: '2026-10-01T09:00:00.000Z',
    },
    corrections: sampleCorrectionsData(scenario),
    people: { [U.a]: 'בקר א (הדגמה)', [U.b]: 'בקר ב (הדגמה)' },
  };
}

export function sampleFollowupEnvelope(mode: 'draft' | 'approved'): ReportEnvelope {
  return {
    reportNumber: 'T-20261001-999999',
    version: 1,
    reviewedAt: '2026-10-01T10:00:00.000Z',
    reviewedByName: 'בקר ב (הדגמה)',
    approval:
      mode === 'draft'
        ? null
        : {
            approvedAt: '2026-10-02T07:00:00.000Z',
            approvedByName: 'בקר ב (הדגמה)',
            participants: [
              { user_id: U.b, name: 'בקר ב (הדגמה)', acts: ['followup', 'reviewed', 'approved'] },
            ],
          },
  };
}
